import axios, { type InternalAxiosRequestConfig, type AxiosError } from "axios";
import { tokenService } from "../services/tokenService";

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface QueuedRequest {
  resolve: (token: unknown) => void;
  reject: (error: unknown) => void;
}

// Store a flash message in localStorage so the login page can show it
// even if the React app hasn't mounted yet when the redirect fires.
const setSessionExpiredFlash = () => {
  try {
    localStorage.setItem(
      "toronq:flash",
      JSON.stringify({ type: "error", message: "Your session has expired. Please sign in again." }),
    );
  } catch {
    // ignore
  }
};

const clearAllClientStorage = () => {
  try {
    localStorage.clear();
  } catch {
    // ignore
  }

  try {
    sessionStorage.clear();
  } catch {
    // ignore
  }
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = tokenService.getAccessToken();
    const requestUrl = config.url || "";

    // Never inject the stored access token on auth endpoints — they are public
    // (login, verify-email, resend, reset-password, etc.) and sending an expired
    // token causes the backend to reject them with "session expired" errors.
    // Note: verify-2fa sets its own tempToken header explicitly.
    const isPublicEndpoint = requestUrl.includes("/auth/");

    if (accessToken && !config.headers.Authorization && !isPublicEndpoint) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error: unknown) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,

  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Never attempt token refresh for auth endpoints — pass the backend error straight through
      const requestUrl = originalRequest.url || "";
      if (requestUrl.includes("/auth/")) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;

            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = tokenService.getRefreshToken();

        if (!refreshToken) {
          throw new Error("No refresh token found");
        }

        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/refresh-login-token`,
          {
            refreshToken,
          },
        );

        const { token: newAccessToken, refreshToken: newRefreshToken } =
          response.data.data;

        tokenService.setTokens(newAccessToken, newRefreshToken);

        processQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);

        tokenService.clearTokens();

        // Session is no longer valid — remove all persisted client state.
        clearAllClientStorage();

        // Show a friendly message on the login page after redirect
        setSessionExpiredFlash();

        window.location.href = "/login";

        // Return a never-resolving promise to prevent error handlers from
        // briefly showing "Token expired" errors before the redirect completes
        return new Promise(() => {});
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;
